import { SUIT_KEYS, TRICK_RANK, COUNTER_RANKS } from './constants';
import { legalPlays, currentWinnerIndex } from './trick';
import { potentialLosers, laydownRoom } from './scoring';

function bySuit(hand) {
  const m = { S: [], H: [], D: [], C: [] };
  for (const c of hand) m[c.suit].push(c);
  return m;
}
function countRank(cards, rank) {
  return cards.filter((c) => c.rank === rank).length;
}

// Evaluate hand and return the max bid this seat is willing to make.
export function evaluateBid(hand, base, difficulty = 'normal', boldness = 'balanced', aggression = 0.65) {
  const m = bySuit(hand);
  let hasMarriage = false;
  let bestSuitScore = 0;
  for (const s of SUIT_KEYS) {
    const cards = m[s];
    const len = cards.length;
    const a = countRank(cards, 'A');
    const t = countRank(cards, '10');
    const k = countRank(cards, 'K');
    const q = countRank(cards, 'Q');
    const j = countRank(cards, 'J');
    const marriage = k > 0 && q > 0;
    if (marriage) hasMarriage = true;
    const run = Math.min(a, t, k, q, j) >= 1 ? 8 : 0;
    const sc = len * 1.6 + a * 3 + t * 1.4 + (marriage ? 4 : 0) + run;
    if (marriage && sc > bestSuitScore) bestSuitScore = sc;
  }
  if (!hasMarriage) return { maxBid: 0 };
  const totalAces = hand.filter((c) => c.rank === 'A').length;
  const score = bestSuitScore + totalAces * 1.4;
  // Convict ("Yard Master") boldness is player-tunable: cautious needs far more hand
  // strength per step, bold plays like the old aggressive AI. Easy bids timidly.
  let div = 3.5;
  let diffAdj = 0;
  if (difficulty === 'easy') {
    diffAdj = -2;
  } else if (difficulty === 'hard') {
    if (boldness === 'cautious') {
      div = 5.0;
      diffAdj = -2;
    } else if (boldness === 'bold') {
      div = 3.5;
      diffAdj = 0;
    } else {
      div = 4.6;
      diffAdj = -1;
    }
  }
  const extra = Math.max(0, Math.floor((score - 15) / div));
  const jitter = Math.random() < 0.4 ? -1 : 0;
  // Character aggression nudges boldness on top of the difficulty baseline
  // (~ -1 for tight/Scrap, 0 for balanced, +1 for loose-aggressive/Baby Boy).
  const aggAdj = Math.max(-2, Math.min(2, Math.round((aggression - 0.55) * 4)));
  const steps = Math.min(Math.max(extra + jitter + diffAdj + aggAdj, 0), 8);
  return { maxBid: base + 5 * steps };
}

export function chooseTrump(hand) {
  const m = bySuit(hand);
  let best = null;
  let bestScore = -1;
  for (const s of SUIT_KEYS) {
    const cards = m[s];
    const marriage = countRank(cards, 'K') > 0 && countRank(cards, 'Q') > 0;
    if (!marriage) continue;
    const score =
      cards.length * 2 + countRank(cards, 'A') * 3 + countRank(cards, '10') * 1.5;
    if (score > bestScore) {
      bestScore = score;
      best = s;
    }
  }
  return best;
}

// Bury 5 lowest-value non-trump cards, keeping trump + aces.
export function chooseDiscards(hand, trump) {
  const scored = hand.map((c) => ({
    c,
    v: TRICK_RANK[c.rank] + (c.suit === trump ? 100 : 0) + (c.rank === 'A' ? 50 : 0),
  }));
  scored.sort((a, b) => a.v - b.v);
  return scored.slice(0, 5).map((x) => x.c.id);
}

export function shouldGoDouble(hand, trump) {
  const trumpLen = hand.filter((c) => c.suit === trump).length;
  const aces = hand.filter((c) => c.rank === 'A').length;
  return trumpLen >= 11 && aces >= 4;
}

// Personality-driven pre-Book-1 concession. Estimates the bidder's book equity and
// folds a weak hand (4+ books below the save floor) at a seat-specific rate:
//   W = DooLow "The Tactician"  -> folds 75% to minimise canteen bleed
//   E = PapaCap "The Stubborn OG" -> folds only 35%, pushes through 65%
export function aiConcede(seat, hand, trump, benchmark, concessionRate = null) {
  const trumps = hand.filter((c) => c.suit === trump);
  const topTrump = trumps.filter((c) => c.rank === 'A' || c.rank === '10').length;
  const offAces = hand.filter((c) => c.rank === 'A' && c.suit !== trump).length;
  const projected = trumps.length * 1.8 + topTrump * 1.2 + offAces * 2.0 + 6;
  const deficit = benchmark - projected;
  if (deficit < 4) return false; // hand is viable — push
  const foldChance = concessionRate != null ? concessionRate : seat === 'W' ? 0.75 : 0.35;
  return Math.random() < foldChance;
}

// Defender decides whether to challenge an exposed lay-down.
// Defender response to a Lay-Down. The bidder's kept hand is face-up, so the defenders read
// its potential losers against the room (TOTAL_POINTS − bench):
//   pressure ≥ 0.85 → borderline claim, always challenge;
//   pressure ≥ 0.65 → challenge only with a hand that can actually set (4+ trump / 5+ Aces);
//   otherwise the lay-down is clearly good — concede.
// Without bidder context (legacy callers) fall back to the raw hand-strength rule.
export function laydownChallenge(hand, trump, ctx) {
  const trumpLen = hand.filter((c) => c.suit === trump).length;
  const aces = hand.filter((c) => c.rank === 'A').length;
  const strong = trumpLen >= 4 || aces >= 5;
  if (!ctx || !ctx.bidderHand) return strong;
  const room = laydownRoom(ctx.bench);
  if (room <= 0) return true;
  const pressure = potentialLosers(ctx.bidderHand, trump) / room;
  if (pressure >= 0.85) return true;
  if (pressure >= 0.65) return strong;
  return false;
}

function lowest(cards) {
  return [...cards].sort((a, b) => TRICK_RANK[a.rank] - TRICK_RANK[b.rank])[0];
}
function highest(cards) {
  return [...cards].sort((a, b) => TRICK_RANK[b.rank] - TRICK_RANK[a.rank])[0];
}
function wouldWin(card, trick, trump, seat) {
  const t = [...trick, { seat, card }];
  return currentWinnerIndex(t, trump) === t.length - 1;
}

function pickStrongSuit(cards, trump) {
  const bs = { S: [], H: [], D: [], C: [] };
  for (const c of cards) if (c.suit !== trump) bs[c.suit].push(c);
  let best = null;
  let bestScore = 0;
  for (const s of SUIT_KEYS) {
    if (s === trump) continue;
    const cs = bs[s];
    const hasAce = cs.some((c) => c.rank === 'A');
    const counters = cs.filter((c) => COUNTER_RANKS.has(c.rank)).length;
    const score = (hasAce ? 5 : 0) + counters * 2 + cs.length;
    if ((hasAce || counters >= 2) && score > bestScore) {
      bestScore = score;
      best = s;
    }
  }
  return best;
}

// "Skillz" tactical rating: probability a defender executes the Anti-Bidder Syndicate
// matrix on any given play (else it falls back to standard suit-following heuristics).
export const SKILL_RATING = { dumptruck: 0.65, alight: 0.75, shooter: 1.0 };

const isVoid = (voids, seat, suit) => !!(voids && voids[seat] && voids[seat].includes(suit));

// Suit voids inferred from the public play log: a legal off-suit play on a non-lead means
// that seat is out of the led suit (defenders track this for both partner and bidder).
export function seatVoids(playLog = []) {
  const v = { W: [], E: [], P: [] };
  for (const e of playLog) {
    if (e.playIndex > 0 && e.legal && e.card.suit !== e.leadCard.suit && !v[e.seat].includes(e.leadCard.suit)) v[e.seat].push(e.leadCard.suit);
  }
  return v;
}
const acesSeen = (played, suit) => (played || []).filter((id) => id.startsWith(`${suit}-A-`)).length;
const nonCounters = (cards) => cards.filter((c) => !COUNTER_RANKS.has(c.rank));

// Anti-Bidder Syndicate: coordinated defensive play against the bidder. Returns a card
// or null (fall through to the standard heuristics).
//   a. Counter Starvation  — never volunteer 10s/Ks under a bidder who still holds the master;
//                            push Q/J to make him burn Aces for nothing.
//   b. Void Exploitation   — lead a suit the partner is void in so he can cut the bidder's Ace.
//   c. Ace-Hunting         — when the contract is on the line, take every book the bidder
//                            is winning with the cheapest winner, even a low trump.
function syndicatePlay(seat, hand, trick, trump, bidWinner, legal, played, ctx) {
  const partner = ['W', 'E', 'P'].find((x) => x !== seat && x !== bidWinner);
  const voids = ctx.voids || {};
  const offSuit = legal.filter((c) => c.suit !== trump);

  if (trick.length === 0) {
    // b. Void exploitation — partner void (and bidder NOT void) in a side suit I hold.
    for (const s of SUIT_KEYS) {
      if (s === trump || !isVoid(voids, partner, s) || isVoid(voids, bidWinner, s)) continue;
      const cards = offSuit.filter((c) => c.suit === s);
      if (cards.length) {
        const cheap = nonCounters(cards);
        return lowest(cheap.length ? cheap : cards);
      }
    }
    // a. Counter starvation — lead an Ace only where it is still the master and the bidder
    //    must follow; otherwise push Queens / Jacks under him.
    const safeAce = offSuit.find((c) => c.rank === 'A' && !isVoid(voids, bidWinner, c.suit) && acesSeen(played, c.suit) < 4);
    if (safeAce) return safeAce;
    const pushers = offSuit.filter((c) => c.rank === 'Q' || c.rank === 'J');
    if (pushers.length) return lowest(pushers);
    const cheap = nonCounters(legal);
    if (cheap.length) return lowest(cheap);
    return null;
  }

  const winIdx = currentWinnerIndex(trick, trump);
  const winnerSeat = trick[winIdx].seat;
  const winners = legal.filter((c) => wouldWin(c, trick, trump, seat));
  const bidderToPlay = !trick.some((p) => p.seat === bidWinner);
  const bidderWinning = winnerSeat === bidWinner;
  const led = trick[0].card.suit;
  const remaining = (ctx.bench ?? 20) - (ctx.bidderBooks ?? 0);
  const onTheLine = remaining <= 6;

  // c. Ace-hunting / book starvation — the bidder is winning: take it as cheaply as possible.
  if (bidderWinning && winners.length) {
    if (onTheLine) return lowest(winners);
    const cheapWin = nonCounters(winners);
    if (cheapWin.length) return lowest(cheapWin);
    return lowest(winners);
  }
  // a. Counter starvation — the bidder still has to play behind me and the master of the
  //    led suit is unaccounted for: do not volunteer a 10 / King he can snatch with an Ace.
  if (bidderToPlay && !bidderWinning) {
    const aceWin = winners.find((c) => c.rank === 'A');
    if (aceWin && led === aceWin.suit) return aceWin;
    const bidderMayHoldAce = led !== trump && acesSeen(played, led) < 4 && !isVoid(voids, bidWinner, led);
    if (bidderMayHoldAce) {
      const cheap = nonCounters(legal);
      if (cheap.length) return lowest(cheap);
    }
  }
  return null;
}

// Convict card-counting bidder lead (trump bleed) — unchanged strategy, extracted for clarity.
function convictBidderLead(hand, legal, trump, played) {
  const trumps = legal.filter((c) => c.suit === trump);
  if (trumps.length) {
    const myTrumps = hand.filter((c) => c.suit === trump).length;
    const seenTrumps = (played || []).filter((id) => id[0] === trump).length;
    const outstanding = Math.max(0, 20 - myTrumps - seenTrumps);
    const topTrump = trumps.some((c) => c.rank === 'A' || c.rank === '10');
    if (outstanding > 0 && (topTrump || trumps.length >= 3)) return highest(trumps);
  }
  const offAcesH = legal.filter((c) => c.rank === 'A' && c.suit !== trump);
  if (offAcesH.length) return offAcesH[0];
  return null;
}

// Naive play (New Fish / Dump Truck lapses): no cooperation, bleeds counters, selfish wins.
function naivePlay(seat, legal, trick, trump) {
  if (trick.length === 0) {
    const nonCounter = nonCounters(legal);
    return lowest(nonCounter.length ? nonCounter : legal);
  }
  const winnersE = legal.filter((c) => wouldWin(c, trick, trump, seat));
  if (winnersE.length && Math.random() < 0.6) return lowest(winnersE);
  const nonCounterE = nonCounters(legal);
  return lowest(nonCounterE.length ? nonCounterE : legal);
}

// AI card selection during trick play. Defenders cooperate against the bidder.
// `played` is the list of card ids seen so far this hand (for Convict card counting).
// `ctx` = { voids: { seat: [suits] }, bidderBooks, bench, skill } — Skillz defensive context.
export function aiPlay(seat, hand, trick, trump, bidWinner, signalSuit, difficulty = 'normal', played = [], renegeRate = 0.02, ctx = {}) {
  const legal = legalPlays(hand, trick, trump);
  if (legal.length === 1) return legal[0];
  const isDefender = bidWinner != null && seat !== bidWinner;

  // Convict ("Yard Master"): AI rarely sneaks an illegal card (renege) mid-trick,
  // daring the human to Call Renege. Kept infrequent so it never bogs down play.
  if (difficulty === 'hard' && trick.length > 0 && renegeRate > 0 && Math.random() < renegeRate) {
    const illegal = hand.filter((c) => !legal.some((l) => l.id === c.id));
    if (illegal.length) {
      const safe = illegal.filter((c) => !COUNTER_RANKS.has(c.rank));
      return lowest(safe.length ? safe : illegal);
    }
  }

  // Easy AI ("New Fish"): naive play, no defender cooperation or signalling.
  if (difficulty === 'easy') return naivePlay(seat, legal, trick, trump);

  // Skillz weighting — defenders roll against their tactical rating on every play.
  if (isDefender) {
    const rating = ctx.skill ? SKILL_RATING[ctx.skill] ?? 1 : 1;
    const optimal = Math.random() < rating;
    if (optimal) {
      const pick = syndicatePlay(seat, hand, trick, trump, bidWinner, legal, played, ctx);
      if (pick) return pick;
    } else if (ctx.skill === 'dumptruck') {
      return naivePlay(seat, legal, trick, trump);
    }
  }

  if (trick.length === 0) {
    // Convict ("Yard Master"): bidder bleeds trump aggressively using card counting.
    if (difficulty === 'hard' && !isDefender) {
      const lead = convictBidderLead(hand, legal, trump, played);
      if (lead) return lead;
    }
    // Defender "come-back": lead the suit partner signalled for, if held.
    if (isDefender && signalSuit) {
      const sig = legal.filter((c) => c.suit === signalSuit);
      if (sig.length) return lowest(sig);
    }
    if (!isDefender) {
      // Bidder trump-draw: with trump dominance (5+ incl. a top trump) strip defenders first.
      const trumps = legal.filter((c) => c.suit === trump);
      const topTrump = trumps.some((c) => c.rank === 'A' || c.rank === '10');
      if (trumps.length >= 5 && topTrump) return highest(trumps);
    }
    // Cash an off-suit Ace to grab counters, else lead a low card.
    const offAces = legal.filter((c) => c.rank === 'A' && c.suit !== trump);
    if (offAces.length) return offAces[0];
    const nonCounter = legal.filter((c) => !COUNTER_RANKS.has(c.rank));
    return lowest(nonCounter.length ? nonCounter : legal);
  }

  const winIdx = currentWinnerIndex(trick, trump);
  const winnerSeat = trick[winIdx].seat;
  const winners = legal.filter((c) => wouldWin(c, trick, trump, seat));
  const nonWinning = legal.filter((c) => !wouldWin(c, trick, trump, seat));
  const partnerWinning = isDefender && winnerSeat !== seat && winnerSeat !== bidWinner;

  if (partnerWinning && nonWinning.length) {
    const led = trick[0].card.suit;
    const voidInLed = !hand.some((c) => c.suit === led);
    // Come-back signal: when truly sloughing, throw a high card (A then J) of a strong side suit.
    if (voidInLed) {
      const strong = pickStrongSuit(nonWinning, trump);
      if (strong) {
        const sigCards = nonWinning.filter((c) => c.suit === strong && (c.rank === 'A' || c.rank === 'J'));
        if (sigCards.length) return sigCards.find((c) => c.rank === 'A') || sigCards[0];
      }
    }
    // Standard feeding: give partner the cheapest counter (K -> 10 -> A), keep top winners.
    const counters = nonWinning.filter((c) => COUNTER_RANKS.has(c.rank));
    if (counters.length) return lowest(counters);
    return lowest(nonWinning);
  }

  if (winners.length) {
    // Take the book with the cheapest winning card (defenders aggressively cut the bidder).
    return lowest(winners);
  }

  // Can't win: never feed counters to whoever is winning — throw the lowest non-counter.
  const nonCounter = legal.filter((c) => !COUNTER_RANKS.has(c.rank));
  return lowest(nonCounter.length ? nonCounter : legal);
}
